/**
 * Shopping Cart Component
 * 
 * Comprehensive cart management with real-time updates, quantity controls,
 * item removal, and checkout integration. Includes responsive design
 * and accessibility features.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useCartStore, cartSelectors } from '../../store/simpleCartStore';
import { wooCommerceProductsAPI } from '../../api/woocommerce/products';
import { Product } from '../../types/ecommerce';
import { Button } from '../Button';
import { LoadingSpinner } from '../LoadingSpinner';

/**
 * Shopping cart props interface
 */
interface ShoppingCartProps {
  /** Whether to show as a full page or sidebar */
  variant?: 'page' | 'sidebar' | 'drawer';
  /** Callback when checkout is initiated */
  onCheckout?: () => void;
  /** Callback when cart is closed (for sidebar/drawer) */
  onClose?: () => void;
  /** Custom CSS classes */
  className?: string;
  /** Whether to show product recommendations */
  showRecommendations?: boolean;
  /** Product data for cart items (optional - will fetch if not provided) */
  products?: Record<string, Product>;
}

/**
 * Cart item component with quantity controls
 */
interface CartItemProps {
  item: {
    productId: string;
    quantity: number;
    variant?: any;
    addedAt: Date;
  };
  product: Product;
  onUpdateQuantity: (productId: string, quantity: number, variantId?: string) => void;
  onRemoveItem: (productId: string, variantId?: string) => void;
  isUpdating: boolean;
}

const CartItem: React.FC<CartItemProps> = ({
  item,
  product,
  onUpdateQuantity,
  onRemoveItem,
  isUpdating,
}) => {
  const [quantity, setQuantity] = useState(item.quantity);
  const [isLocalUpdating, setIsLocalUpdating] = useState(false);

  // Calculate price (considering variants)
  const itemPrice = item.variant?.price || product.price;
  const totalPrice = itemPrice * quantity;

  // Get primary product image
  const primaryImage = product.images[0];
  const imageUrl = primaryImage?.src || '/placeholder-product.jpg';
  const imageAlt = primaryImage?.alt || product.name;

  /**
   * Handle quantity change with debouncing
   */
  const handleQuantityChange = useCallback(async (newQuantity: number) => {
    if (newQuantity < 1) return;
    
    setQuantity(newQuantity);
    setIsLocalUpdating(true);

    try {
      await onUpdateQuantity(item.productId, newQuantity, item.variant?.id);
    } catch (error) {
      // Revert on error
      setQuantity(item.quantity);
      console.error('Failed to update quantity:', error);
    } finally {
      setIsLocalUpdating(false);
    }
  }, [item.productId, item.quantity, item.variant?.id, onUpdateQuantity]);

  /**
   * Handle item removal
   */
  const handleRemove = useCallback(async () => {
    setIsLocalUpdating(true);
    try {
      await onRemoveItem(item.productId, item.variant?.id);
    } catch (error) {
      console.error('Failed to remove item:', error);
      setIsLocalUpdating(false);
    }
  }, [item.productId, item.variant?.id, onRemoveItem]);

  /**
   * Handle direct quantity input
   */
  const handleQuantityInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuantity = parseInt(e.target.value, 10);
    if (!isNaN(newQuantity) && newQuantity >= 1) {
      handleQuantityChange(newQuantity);
    }
  }, [handleQuantityChange]);

  return (
    <div className="flex items-start gap-4 p-4 border-b border-gray-200 last:border-b-0">
      {/* Product Image */}
      <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20">
        <img
          src={imageUrl}
          alt={imageAlt}
          className="w-full h-full object-cover rounded-md"
          loading="lazy"
        />
      </div>

      {/* Product Details */}
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-medium text-gray-900 truncate">
          {product.name}
        </h3>
        
        {/* Variant Information */}
        {item.variant && (
          <p className="text-xs text-gray-500 mt-1">
            {Object.entries(item.variant.attributes).map(([key, value]) => 
              `${key}: ${value}`
            ).join(', ')}
          </p>
        )}

        {/* Price */}
        <p className="text-sm font-medium text-gray-900 mt-1">
          ${itemPrice.toFixed(2)} each
        </p>

        {/* Stock Warning */}
        {product.inventory.stockQuantity > 0 && 
         product.inventory.stockQuantity < quantity && (
          <p className="text-xs text-red-600 mt-1">
            Only {product.inventory.stockQuantity} in stock
          </p>
        )}
      </div>

      {/* Quantity Controls */}
      <div className="flex flex-col items-end gap-2">
        <div className="flex items-center gap-1">
          <Button
            onClick={() => handleQuantityChange(quantity - 1)}
            disabled={quantity <= 1 || isUpdating || isLocalUpdating}
            variant="outline"
            size="xs"
            className="w-6 h-6 p-0"
          >
            -
          </Button>
          
          <input
            type="number"
            value={quantity}
            onChange={handleQuantityInput}
            disabled={isUpdating || isLocalUpdating}
            min="1"
            max={product.inventory.stockQuantity || 999}
            className="w-12 h-6 text-center text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          
          <Button
            onClick={() => handleQuantityChange(quantity + 1)}
            disabled={
              isUpdating || 
              isLocalUpdating || 
              (product.inventory.stockQuantity > 0 && quantity >= product.inventory.stockQuantity)
            }
            variant="outline"
            size="xs"
            className="w-6 h-6 p-0"
          >
            +
          </Button>
        </div>

        {/* Total Price */}
        <p className="text-sm font-medium text-gray-900">
          ${totalPrice.toFixed(2)}
        </p>

        {/* Remove Button */}
        <Button
          onClick={handleRemove}
          disabled={isUpdating || isLocalUpdating}
          variant="ghost"
          size="xs"
          className="text-red-600 hover:text-red-700 p-1"
        >
          {isLocalUpdating ? (
            <LoadingSpinner size="xs" />
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          )}
        </Button>
      </div>
    </div>
  );
};

/**
 * Cart summary component with totals and checkout
 */
interface CartSummaryProps {
  subtotal: number;
  itemCount: number;
  onCheckout: () => void;
  isLoading: boolean;
}

const CartSummary: React.FC<CartSummaryProps> = ({
  subtotal,
  itemCount,
  onCheckout,
  isLoading,
}) => {
  // Calculate estimated tax and shipping (would come from API in real implementation)
  const estimatedTax = subtotal * 0.08; // 8% tax
  const estimatedShipping = subtotal > 50 ? 0 : 9.99; // Free shipping over $50
  const total = subtotal + estimatedTax + estimatedShipping;

  return (
    <div className="bg-gray-50 p-4 rounded-lg">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Order Summary</h3>
      
      {/* Line Items */}
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span>Subtotal ({itemCount} items)</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between">
          <span>Estimated Tax</span>
          <span>${estimatedTax.toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between">
          <span>Shipping</span>
          <span>
            {estimatedShipping === 0 ? (
              <span className="text-green-600 font-medium">Free</span>
            ) : (
              `$${estimatedShipping.toFixed(2)}`
            )}
          </span>
        </div>
        
        {subtotal > 0 && subtotal < 50 && (
          <p className="text-xs text-gray-600">
            Add ${(50 - subtotal).toFixed(2)} more for free shipping
          </p>
        )}
      </div>

      {/* Total */}
      <div className="border-t border-gray-200 mt-4 pt-4">
        <div className="flex justify-between text-lg font-medium">
          <span>Total</span>
          <span>${total.toFixed(2)}</span>
        </div>
      </div>

      {/* Checkout Button */}
      <Button
        onClick={onCheckout}
        disabled={isLoading || subtotal === 0}
        variant="primary"
        size="lg"
        className="w-full mt-4"
      >
        {isLoading ? (
          <div className="flex items-center gap-2">
            <LoadingSpinner size="sm" />
            Processing...
          </div>
        ) : (
          `Proceed to Checkout`
        )}
      </Button>

      {/* Security Badge */}
      <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-500">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <span>Secure checkout</span>
      </div>
    </div>
  );
};

/**
 * Main Shopping Cart Component
 */
export const ShoppingCart: React.FC<ShoppingCartProps> = ({
  variant = 'page',
  onCheckout,
  onClose,
  className = '',
  products: propProducts,
}) => {
  const [products, setProducts] = useState<Record<string, Product>>(propProducts || {});
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // Cart store selectors
  const items = useCartStore(cartSelectors.items);
  const itemCount = useCartStore(cartSelectors.itemCount);
  const isLoading = useCartStore(cartSelectors.isLoading);
  const error = useCartStore(cartSelectors.error);
  
  // Cart actions
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const removeItem = useCartStore((state) => state.removeItem);
  const clearCart = useCartStore((state) => state.clearCart);

  /**
   * Load product details for cart items
   */
  React.useEffect(() => {
    // If products are passed as props, use them
    if (propProducts) {
      setProducts(propProducts);
      setIsLoadingProducts(false);
      return;
    }

    // Otherwise, try to fetch from API
    const loadProducts = async () => {
      if (items.length === 0) return;

      setIsLoadingProducts(true);
      const productMap: Record<string, Product> = {};

      try {
        await Promise.all(
          items.map(async (item) => {
            if (!products[item.productId]) {
              const product = await wooCommerceProductsAPI.getProduct(item.productId);
              if (product) {
                productMap[item.productId] = product;
              }
            }
          })
        );

        setProducts((prev) => ({ ...prev, ...productMap }));
      } catch (error) {
        console.error('Failed to load product details:', error);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    loadProducts();
  }, [items, products, propProducts]);

  /**
   * Calculate cart totals
   */
  const cartTotals = useMemo(() => {
    let subtotal = 0;
    let count = 0;

    items.forEach((item) => {
      const product = products[item.productId];
      if (product) {
        const price = item.variant?.price || product.price;
        subtotal += price * item.quantity;
        count += item.quantity;
      }
    });

    return { subtotal, count };
  }, [items, products]);

  /**
   * Handle checkout action
   */
  const handleCheckout = useCallback(() => {
    if (onCheckout) {
      onCheckout();
    } else {
      // Default checkout behavior - could navigate to checkout page
      console.log('Proceeding to checkout with items:', items);
    }
  }, [onCheckout, items]);

  /**
   * Handle clear cart
   */
  const handleClearCart = useCallback(async () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      await clearCart();
    }
  }, [clearCart]);

  // Container classes based on variant
  const containerClasses = {
    page: 'max-w-4xl mx-auto p-4',
    sidebar: 'h-full flex flex-col',
    drawer: 'h-full flex flex-col',
  };

  // Loading state
  if (isLoadingProducts && items.length > 0) {
    return (
      <div className={`${containerClasses[variant]} ${className}`}>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">Loading cart...</span>
        </div>
      </div>
    );
  }

  // Empty cart state
  if (items.length === 0) {
    return (
      <div className={`${containerClasses[variant]} ${className}`}>
        {variant !== 'page' && onClose && (
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-medium">Shopping Cart</h2>
            <Button onClick={onClose} variant="ghost" size="sm">
              ✕
            </Button>
          </div>
        )}
        
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="text-gray-400 text-6xl mb-4">🛒</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Your cart is empty</h3>
          <p className="text-gray-600 mb-4">Add some products to get started!</p>
          {variant !== 'page' && onClose && (
            <Button onClick={onClose} variant="primary">
              Continue Shopping
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${containerClasses[variant]} ${className}`}>
      {/* Header */}
      {variant !== 'page' && onClose && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-medium">Shopping Cart ({itemCount})</h2>
          <Button onClick={onClose} variant="ghost" size="sm">
            ✕
          </Button>
        </div>
      )}

      {variant === 'page' && (
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Shopping Cart ({itemCount})</h1>
          {items.length > 0 && (
            <Button onClick={handleClearCart} variant="outline" size="sm">
              Clear Cart
            </Button>
          )}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Cart Content */}
      <div className={`${variant === 'page' ? 'grid grid-cols-1 lg:grid-cols-3 gap-8' : 'flex flex-col'}`}>
        {/* Cart Items */}
        <div className={variant === 'page' ? 'lg:col-span-2' : 'flex-1 overflow-auto'}>
          <div className="bg-white border border-gray-200 rounded-lg">
            {items.map((item) => {
              const product = products[item.productId];
              if (!product) {
                return (
                  <div key={`${item.productId}-${item.variant?.id || 'default'}`} className="p-4 border-b border-gray-200">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-gray-200 rounded-md animate-pulse" />
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded animate-pulse mb-2" />
                        <div className="h-3 bg-gray-200 rounded animate-pulse w-24" />
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <CartItem
                  key={`${item.productId}-${item.variant?.id || 'default'}`}
                  item={item}
                  product={product}
                  onUpdateQuantity={updateQuantity}
                  onRemoveItem={removeItem}
                  isUpdating={isLoading}
                />
              );
            })}
          </div>
        </div>

        {/* Cart Summary */}
        <div className={variant === 'page' ? '' : 'border-t border-gray-200 p-4'}>
          <CartSummary
            subtotal={cartTotals.subtotal}
            itemCount={cartTotals.count}
            onCheckout={handleCheckout}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default ShoppingCart;